import { getCurrentScreen } from "@auth0/auth0-acul-js";
import LoginInstance from "@auth0/auth0-acul-js/login";
import SignupInstance from "@auth0/auth0-acul-js/signup";
import type { UniversalLoginContext } from "../../types/universal-login";

const DEFAULT_SCREEN_NAME = "login";

type ContextModule = { default: UniversalLoginContext };
const mockContextModules = import.meta.glob<ContextModule>(
  "../../mock-data/*.json"
);

const unique = (values: Array<string | null | undefined>): string[] => {
  const seen = new Set<string>();
  values.forEach((value) => {
    if (value) {
      seen.add(value);
    }
  });
  return Array.from(seen);
};

const LOCALHOST_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);

const loadDevContext = async (
  screenName: string
): Promise<UniversalLoginContext> => {
  const importPath = `../../mock-data/${screenName}.json`;
  const loader = mockContextModules[importPath];

  if (!loader) {
    throw new Error(
      `[DEV] Mock context "${screenName}" not found. Add src/mock-data/${screenName}.json.`
    );
  }

  const module = await loader();
  console.info(`[DEV] Loaded mock Universal Login context "${screenName}".`);
  return module.default;
};

const canUseMockContext = (): boolean => {
  if (import.meta.env.DEV) {
    return true;
  }

  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname;
  return (
    LOCALHOST_HOSTNAMES.has(hostname) && !window.universal_login_context
  );
};

export const ensureUniversalLoginContext = async (
  desiredScreenName?: string
): Promise<void> => {
  if (typeof window === "undefined") {
    return;
  }

  let currentScreenName =
    window.universal_login_context?.screen?.name ?? undefined;

  if (!currentScreenName) {
    try {
      currentScreenName = getCurrentScreen() ?? undefined;
    } catch (error) {
      console.warn(
        "[DEV] Unable to resolve current screen from SDK before context initialization.",
        error
      );
    }
  }

  const shouldLoadMockContext = canUseMockContext();

  if (!shouldLoadMockContext) {
    if (!window.universal_login_context) {
      throw new Error(
        "Universal Login Context is missing. Ensure the host page injects `window.universal_login_context` before mounting this bundle."
      );
    }

    if (
      currentScreenName &&
      desiredScreenName &&
      currentScreenName !== desiredScreenName
    ) {
      throw new Error(
        `Universal Login Context mismatch. Expected "${desiredScreenName}", but found "${currentScreenName}".`
      );
    }
    return;
  }

  if (!shouldLoadMockContext) {
    return;
  }

  const requestedScreens = unique([
    desiredScreenName?.trim(),
    import.meta.env.VITE_SCREEN_NAME?.trim(),
    currentScreenName,
    DEFAULT_SCREEN_NAME,
  ]);

  if (requestedScreens.length === 0) {
    throw new Error("[DEV] No candidate screen provided for mock context.");
  }

  let lastError: unknown;
  for (const screenName of requestedScreens) {
    try {
      const context = await loadDevContext(screenName);
      window.universal_login_context = context;
      resetSDKContextCache();
      return;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("[DEV] Unable to load a mock Universal Login context.");
};

const resetSDKContextCache = () => {
  const candidates = [
    Object.getPrototypeOf(LoginInstance),
    Object.getPrototypeOf(SignupInstance),
    LoginInstance,
    SignupInstance,
  ];

  candidates.forEach((ctor) => {
    if (ctor && typeof ctor === "function" && "context" in ctor) {
      (ctor as { context?: UniversalLoginContext | null }).context = null;
    }
  });
};
